import WebApp from '@twa-dev/sdk';

import {
  EXCLUSIVE_MARKET_SOURCE,
  LIGHT_MARKET_SOURCE,
} from '@/app/lib/localization/constants';

import { usePlaydeckStoreRaw } from '@/entities/playdeck/models/playdeck.store';

import { ApiService } from '../api/ApiService';
import { getIsDev } from '../helpers/environment/get-is-dev';
import { getUIKit } from '../helpers/environment/get-ui-kit';
import { getDecodedStartappQuery } from '../helpers/telegram/get-decoded-startapp-query';
import { BaseLogger } from '../service/logger/BaseLogger';
import { ErrorLogger } from '../service/logger/ErrorLogger';
import { useAppStateRaw } from '../stores/app-store/app-store';
import { type InitialState, type ThemeMode, type UIKit } from '../stores/app-store/types';
import { getInitialAppState } from '../stores/app-store/utils/get-initial-app-state';

const setThemeForDOM = (theme: ThemeMode) => {
  document.documentElement.setAttribute('data-theme', theme);
};
const setUIKit = (uiKit: UIKit) => {
  document.documentElement.setAttribute('ui-kit', uiKit);
};

const requestDevHash = () => {
  return new Promise<string>((resolve) => {
    if (import.meta.env.VITE_APP_TEST_HASH) {
      resolve(import.meta.env.VITE_APP_TEST_HASH);
      return;
    }
    const token = prompt(
      'Get telegram context: insert copy(window.location.hash) in telegram console.\nImportant. If you have a dev link specified, then the context should be from the replay bot.'
    );

    resolve(token || '');
  });
};

export const initApplication = async () => {
  let initialState = {} as InitialState;
  const IS_DEV = getIsDev();

  if (!window.location.hash && IS_DEV && !import.meta.env.VITE_APP_DEV_TOKEN) {
    const hash = await requestDevHash();
    window.location.hash = hash;
    window.location.reload();
    //TODO: Added logging
  }

  try {
    const startappQuery = getDecodedStartappQuery();

    const { actions } = useAppStateRaw.getState();

    actions.setIsLiteMode(startappQuery.src === LIGHT_MARKET_SOURCE);
    actions.setIsExclusiveMode(startappQuery.src === EXCLUSIVE_MARKET_SOURCE);

    await BaseLogger.init();

    const token = await ApiService.init();

    initialState = await getInitialAppState(token);
    useAppStateRaw.getState().actions.hydrate(initialState);

    setUIKit(getUIKit());
    setThemeForDOM(WebApp.colorScheme as ThemeMode);

    const { decodedToken } = initialState;

    if (!decodedToken) throw new Error('Decoded token is not defined');

    useAppStateRaw.getState().actions.loadGame(decodedToken);
    useAppStateRaw.getState().actions.fetchUserProfile();

    usePlaydeckStoreRaw.getState().actions.setTheme({ gameKey: startappQuery.game?.key });

    WebApp.ready();
  } catch (error) {
    ErrorLogger.error(error as Error, 'initApplication function');

    throw error;
  }

  return initialState;
};
